import { getPlatform } from './getPlatform';

// Crypto işlemleri için yardımcı fonksiyonlar
const CryptoHelper = {
  isAvailable() {
    return typeof crypto !== 'undefined';
  },

  getRandomValues(array) {
    if (this.isAvailable()) {
      return crypto.getRandomValues(array);
    }
    return null;
  },

  getNativeUUID() {
    return this.isAvailable() && crypto.randomUUID ? crypto.randomUUID() : null;
  },

  getSecureRandom() {
    if (this.isAvailable()) {
      const array = new Uint32Array(1);
      this.getRandomValues(array);
      return array[0] / (2 ** 32 - 1);
    }
    return Date.now() / 1000000;
  }
};

// Sistem bilgilerini toplayan sınıf
class SystemInfoCollector {
  constructor() {
    this.navigator = typeof window !== 'undefined' ? window.navigator : null;
    this.platform = getPlatform();
  }

  getBasicInfo() {
    return {
      os: this.platform.os,
      browser: this.platform.browser,
      browserVersion: this.platform.version
    };
  }

  getScreenInfo() {
    return {
      screenWidth: window?.screen?.width ?? 0,
      screenHeight: window?.screen?.height ?? 0,
      colorDepth: window?.screen?.colorDepth ?? 0,
      pixelRatio: window?.devicePixelRatio ?? 1
    };
  }

  getHardwareInfo() {
    return {
      deviceMemory: this.navigator?.deviceMemory ?? 0,
      hardwareConcurrency: this.navigator?.hardwareConcurrency ?? 0,
      maxTouchPoints: this.navigator?.maxTouchPoints ?? 0
    };
  }

  getLocalizationInfo() {
    return {
      language: this.navigator?.language ?? '',
      languages: this.navigator?.languages ?? [],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }

  getConnectionInfo() {
    const connection = this.navigator?.connection;
    return {
      effectiveType: connection?.effectiveType ?? '',
      downlink: connection?.downlink ?? 0,
      rtt: connection?.rtt ?? 0,
      saveData: connection?.saveData ?? false
    };
  }

  getPerformanceInfo() {
    const getNavigationTiming = () => {
      if (window?.performance?.getEntriesByType) {
        const navigationEntry = performance.getEntriesByType('navigation')[0];
        return navigationEntry ? navigationEntry.startTime : 0;
      }
      return 0;
    };

    return {
      navigationStart: getNavigationTiming(),
      performanceNow: window?.performance?.now() ?? 0
    };
  }

  getAllInfo() {
    return {
      ...this.getBasicInfo(),
      ...this.getScreenInfo(),
      ...this.getHardwareInfo(),
      ...this.getLocalizationInfo(),
      ...this.getConnectionInfo(),
      ...this.getPerformanceInfo(),
      timestamp: Date.now(),
      randomSeed: CryptoHelper.getSecureRandom()
    };
  }
}

// Hash oluşturucu sınıf
class HashGenerator {
  static generate(data) {
    const combinedInfo = JSON.stringify(data);
    let hashValue = 0n;
    const strLen = combinedInfo.length;
    const prime1 = 31n;
    const prime2 = 37n;

    for (let i = 0; i < strLen; i++) {
      const char = BigInt(combinedInfo.charCodeAt(i));
      hashValue = ((hashValue + char) * prime1) % 2n ** 64n;
      hashValue = (hashValue * prime2) % 2n ** 64n;
    }

    // Handle large BigInt values safely
    if (hashValue < Number.MAX_SAFE_INTEGER) {
      return Number(hashValue).toString(36);
    }

    // Convert BigInt to string safely
    const convertBigIntToBase36 = bigInt => {
      // Convert to decimal string first
      const decimalString = bigInt.toString();
      // Then convert to number and base36
      return parseInt(decimalString).toString(36);
    };

    // For large values, split and convert each chunk
    const chunk1 = (hashValue >> 32n) & 0xffffffffn;
    const chunk2 = hashValue & 0xffffffffn;

    return `${convertBigIntToBase36(chunk1)}-${convertBigIntToBase36(chunk2)}`;
  }

  static getSecureRandomPart() {
    if (CryptoHelper.isAvailable()) {
      const array = new Uint32Array(1);
      CryptoHelper.getRandomValues(array);
      return BigInt(array[0]).toString(36).padStart(6, '0');
    }
    return BigInt(Date.now()).toString(36).padStart(6, '0');
  }
}

// Ana UUID oluşturucu sınıf
class UUIDGenerator {
  static generateCustomUUID() {
    try {
      const systemInfo = new SystemInfoCollector();
      const allInfo = systemInfo.getAllInfo();
      const finalHash = HashGenerator.generate(allInfo);
      const timestamp = BigInt(Date.now()).toString(36);
      const randomPart = HashGenerator.getSecureRandomPart();
      const prefix = `${allInfo.os}-${allInfo.browser}`.toLowerCase();

      return `${prefix}-${timestamp}-${finalHash}-${randomPart}`;
    } catch (error) {
      return this.generateFallbackUUID();
    }
  }

  static generateFallbackUUID() {
    const getFallbackSecureId = () => {
      if (CryptoHelper.isAvailable()) {
        const array = new Uint32Array(2);
        CryptoHelper.getRandomValues(array);
        return Array.from(array)
          .map(n => n.toString(36))
          .join('')
          .substring(2, 11);
      }
      return Date.now().toString(36);
    };

    const platform = getPlatform();
    const prefix = `${platform.os}-${platform.browser}`.toLowerCase();
    const timestamp = Date.now().toString(36);
    const random = getFallbackSecureId();

    return `${prefix}-fallback-${timestamp}-${random}`;
  }

  static generateCryptoUUID() {
    const nativeUUID = CryptoHelper.getNativeUUID();
    if (nativeUUID) return nativeUUID;

    if (CryptoHelper.isAvailable()) {
      const buffer = new Uint8Array(16);
      CryptoHelper.getRandomValues(buffer);

      buffer[6] = (buffer[6] & 0x0f) | 0x40;
      buffer[8] = (buffer[8] & 0x3f) | 0x80;

      const segments = [
        buffer.slice(0, 4),
        buffer.slice(4, 6),
        buffer.slice(6, 8),
        buffer.slice(8, 10),
        buffer.slice(10, 16)
      ].map(segment =>
        Array.from(segment)
          .map(b => b.toString(16).padStart(2, '0'))
          .join('')
      );

      return segments.join('-');
    }

    return this.generateCustomUUID();
  }
}

export function generateUUID() {
  return UUIDGenerator.generateCryptoUUID();
}
